<script lang="ts">
    import {persistentStorageLocal} from 'shared/persistent-storage';
    import type {DraggableResizerEventData} from './erp-draggable-resizer/types';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {get, writable} from 'svelte/store';
    import {onDestroy, tick} from 'svelte';
    import ErpDraggableResizer from './erp-draggable-resizer/ErpDraggableResizer.svelte';

    export let orientation: 'horizontal' | 'vertical';
    export let ratios: [number, number] = [1, 1];
    export let withoutPanelPadding = false;
    export let withoutScroll = false;
    export let resizeDisabled = false;
    export let persistentStorageKeyPrefix: string | null = null;

    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;

    const baseSecondPanelRatio = ratios[1] / (ratios[0] + ratios[1]);
    let secondPanelContainer: HTMLDivElement;
    const secondPanelRatio$ = writable(baseSecondPanelRatio);
    const persistentStorageKey = `${persistentStorageKeyPrefix}-${orientation === 'horizontal' ? 'right' : 'bottom'}-panel-${orientation === 'horizontal' ? 'width' : 'height'}-ratio`;
    const secondPanelSizePersistentUnsubscribe = !exists(persistentStorageKeyPrefix) || resizeDisabled ? undefined : persistentStorageLocal(secondPanelRatio$, persistentStorageKey);
    let beforeResizingSecondPanelSize: number | null = null;

    onDestroy(() => {
        cleanup(secondPanelSizePersistentUnsubscribe);
    });

    const handleResize = (event: CustomEvent<DraggableResizerEventData>) => {
        if (!exists(beforeResizingSecondPanelSize) && event.detail.resizing) {
            beforeResizingSecondPanelSize = get(secondPanelRatio$) * (orientation === 'horizontal' ? windowWidth : windowHeight);
        }

        if (!event.detail.resizing) {
            beforeResizingSecondPanelSize = null;

            if (exists(secondPanelContainer)) {
                tick().then(() => $secondPanelRatio$ = secondPanelContainer.getBoundingClientRect()[orientation === 'horizontal' ? 'width' : 'height'] / (orientation === 'horizontal' ? windowWidth : windowHeight));
            }
        }

        if (!exists(beforeResizingSecondPanelSize) && !event.detail.resizing) {
            return;
        }

        $secondPanelRatio$ = (beforeResizingSecondPanelSize - event.detail.delta) / (orientation === 'horizontal' ? windowWidth : windowHeight);
    };

    const handleReset = () => {
        $secondPanelRatio$ = baseSecondPanelRatio;
    };
</script>

<div class="split-content-container orientation-{orientation}"
     bind:clientWidth={windowWidth}
     bind:clientHeight={windowHeight}
     class:resize-disabled={resizeDisabled}
     class:without-panel-padding={withoutPanelPadding}
     class:without-scroll={withoutScroll}>

    <div class="panel-container first" style:--resize-disabled-ratio={ratios[0]}>
        <div class="panel-inner first">
            <slot name="first-panel"/>
        </div>
    </div>

    <ErpDraggableResizer disabled={resizeDisabled}
                         on:resize={handleResize}
                         on:reset={handleReset}
                         orientation={orientation === 'horizontal' ? 'vertical' : 'horizontal'}/> <!-- Orientation of resizer is opposite to the orientation of the split layout -->

    <div class="panel-container second"
         style:--resize-disabled-ratio={ratios[1]}
         style:--second-panel-width={orientation === 'horizontal' ? `${$secondPanelRatio$ * windowWidth}px` : '100%'}
         style:--second-panel-height={orientation === 'vertical' ? `${$secondPanelRatio$ * windowHeight}px` : '100%'}
         bind:this={secondPanelContainer}>

        <div class="panel-inner second">
            <slot name="second-panel"/>
        </div>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro-erp.less";
    @import (reference) "styles/portaro.variables.less";

    .split-content-container {
        .flex-grow();
        overflow: hidden;

        &.orientation-horizontal {
            flex-direction: row;
        }

        &.orientation-vertical {
            flex-direction: column;
        }

        &.resize-disabled .panel-container {
            flex: var(--resize-disabled-ratio);
        }

        &:not(.resize-disabled) .panel-container.second {
            width: var(--second-panel-width);
            height: var(--second-panel-height);
        }

        &.without-panel-padding .panel-container .panel-inner {
            padding: 0;
        }

        &.without-scroll .panel-container {
            overflow: hidden;
        }

        .panel-container {
            overflow-y: auto;
            display: flex;
            flex-direction: column;

            &.first {
                .flex-grow();
            }

            .panel-inner {
                .flex-grow();
                padding: @spacing-l @spacing-xl;
            }
        }
    }
</style>